import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button } from '@resy/ui-kitchen';
import Minus from '../../../../images/amexgdab2b/minus-circle.svg';
import Plus from '../../../../images/amexgdab2b/plus-circle.svg';
import { getRetinaImage, handleMktAnalytics } from '../../AmexUtils';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { CTA_EVENT_NAME } from '../../constants';
import './AmexExpandableSection.scss';

const MAX_SECTIONS = 5;

const AmexExpandableSection = (
  {
    awsFolderName,
    baseRequestUrl,
    sectionConfig: {
      subtitle,
      copy,
      section_image: sectionImage = {},
      image_position: imagePosition,
      content = [],
      section_image_height: sectionImgHeight,
      expanded_section_copy_height: expandCopyHeight,
    } = {},
    matchMedia,
    id: sectionId,
    eventAnalytics,
  }
) => {
  if (content.length === 0 || !sectionImgHeight || !expandCopyHeight) return false;
  const [activeIndex, setActiveIndex] = useState(0);

  const {
    desktop: desktopImg = {},
    mobile: mobileImg = {},
  } = sectionImage;
  const hasSectionImage = Object.keys(sectionImage).length !== 0;
  const requiredKeys = hasSectionImage ? ['id', 'heading', 'description'] : ['id', 'heading', 'description', 'image'];

  const validatedSections = content
    .filter((item) => requiredKeys.every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]));
  const sections = validatedSections?.filter((obj, index) => index === validatedSections
    .findIndex((o) => obj.id === o.id)).slice(0, MAX_SECTIONS);

  const sectionsLen = sections?.length || 0;
  if (sectionsLen === 0) return false;

  const isMobile = !matchMedia.lg;
  const sectionImg = getRetinaImage(desktopImg, mobileImg, matchMedia.md) || null;
  const activeImage = () => sections[activeIndex].image;
  const finalImage = hasSectionImage ? sectionImg : activeImage();

  const renderIcon = (id) => (
    <span aria-hidden="true">
      {activeIndex === id ? (
        <Minus className="AmexExpandableSection__icon" />
      ) : (
        <Plus className="AmexExpandableSection__icon" />
      )}
    </span>
  );

  const setActiveSection = (sectionIndex) => {
    if (sectionIndex !== activeIndex) {
      setActiveIndex(sectionIndex);
    } else {
      const selectedSectionIdx = sections.indexOf(sections[sectionIndex]);
      const isLastSection = sections.length - 1 === selectedSectionIdx;
      const newActiveSectionIndex = isLastSection ? 0 : selectedSectionIdx + 1;
      setActiveIndex(newActiveSectionIndex);
    }
  };

  const renderSectionsContent = () => sections
    .map((section, index) => {
      const sanitizedHeading = sanitizeString(section.heading);
      const sanitizedDescription = sanitizeString(section.description);

      const isActive = activeIndex === index;

      return (
        <li
          key={section.description}
          className={cx('AmexExpandableSection__accordion--item', {
            active: isActive,
            single: sectionsLen === 1,
          })}
        >
          {(section.heading && sectionsLen > 1) && (
            <h4 className="AmexExpandableSection__accordion--title">
              <Button
                variant="no-style"
                type="button"
                id={`${sectionId}Title${index}`}
                aria-controls={`${sectionId}Content${index}`}
                className="AmexExpandableSection__accordion--button"
                aria-expanded={isActive}
                onClick={() => {
                  const extraPrams = {
                    action_name: 'Expandable Click',
                    item_name: sanitizedHeading,
                  };
                  handleMktAnalytics(eventAnalytics, CTA_EVENT_NAME, extraPrams);
                  setActiveSection(index);
                }}
              >
                {renderIcon(index)}
                <span
                  dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                    __html: sanitizedHeading,
                  }}
                />
              </Button>
            </h4>
          )}

          {(section.heading && sectionsLen === 1) && (
            <h4
              className="AmexExpandableSection__accordion--title"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedHeading,
              }}
            />
          )}

          <div
            role="region"
            id={`${sectionId}Content${index}`}
            aria-labelledby={`${sectionId}Title${index}`}
            className="AmexExpandableSection__accordion--content"
          >
            <div className="AmexExpandableSection__inner">
              <div
                className="AmexExpandableSection__text"
                {...(!isMobile ? {
                  style: { minHeight: expandCopyHeight },
                } : {})}
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizedDescription,
                }}
              />
              {(isMobile && !hasSectionImage) && (
                <img
                  className="AmexExpandableSection__mobile-img"
                  src={`${baseRequestUrl}${awsFolderName}/images/${finalImage}`}
                  alt=""
                />
              )}
            </div>
          </div>
        </li>
      );
    });

  const sanitizedSubtitle = sanitizeString(subtitle);
  const sanitizedCopy = sanitizeString(copy);

  return (
    <div className={`AmexExpandableSection image-${imagePosition}`}>
      <div className="AmexExpandableSection__wrapper">
        <div className="AmexExpandableSection__top-copy">
          {subtitle && (
            <h3
              className="AmexExpandableSection__section-title"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedSubtitle,
              }}
            />
          )}
          {copy && (
            <p
              className="AmexExpandableSection__copy"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedCopy,
              }}
            />
          )}

          {(isMobile && hasSectionImage && sectionImg) && (
            <img
              className="AmexExpandableSection__mobile-img"
              src={`${baseRequestUrl}${awsFolderName}/images/${finalImage}`}
              alt=""
            />
          )}
        </div>
        <ol className="AmexExpandableSection__accordion">
          {sections && renderSectionsContent()}
        </ol>
      </div>
      {!isMobile && (
        <div
          className="AmexExpandableSection__image"
          style={{
            minHeight: sectionImgHeight,
            backgroundImage: `url(${baseRequestUrl}SecurityCenter/images/${finalImage})`,
          }}
        />
      )}
    </div>
  );
};

AmexExpandableSection.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.arrayOf(PropTypes.shape(
    {
      id: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
      section_image_height: PropTypes.string,
      expanded_section_copy_height: PropTypes.string,
      content: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        heading: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        image: PropTypes.string,
      })).isRequired,
    }
  )).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  eventAnalytics: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default AmexExpandableSection;
